/** Part IV — People and places: townsfolk, town life, money, groups and cities, politics and war, talking. */
import type { HelpPart } from './types.js';

export const PART_SOCIETY: HelpPart = {
  numeral: 'IV',
  title: 'People and places',
  chapters: [
    {
      id: 'people',
      title: 'The townsfolk',
      blurb: 'Who lives in the towns, how to talk with them, and what they remember about you.',
      pages: [
        {
          id: 'people/who',
          title: 'Who the townsfolk are',
          terms: ['versona', 'npc', 'jobs', 'family', 'graves', 'schedule'],
          body: `<p>Every person in a town is a <b>versona</b>: someone with a name, a home, a trade, a household, moods,
          memories, opinions and a day of their own. They work their trade, trade at the market, eat, sleep, gather at the
          tavern, argue about the levy, marry, raise children, grow old and die — and are buried in the cemetery outside
          their town, where their names stay.</p>
          <p>Their trades are a dozen base jobs; what they become known for (a healer, a baker, a lamplighter) comes from the
          skills they actually practise. Some hold offices — mayor, keeper, ministers of the Assembly.</p>
          <p>They notice you the way strangers do: a glance when you pass, a longer look if they know you, a stare only if
          you crowd them. Two of them talking show what they say in speech bubbles — walk close enough and you can
          overhear. With voices on (Options → Sound) they speak aloud, each in a voice that fits them.</p>`,
          see: ['people/talk'],
        },
        {
          id: 'people/talk',
          title: 'Talking',
          terms: ['talk', 't', 'topics', 'rumors', 'disposition', 'conversation'],
          body: `<p>Stand near a townsperson and press <kbd>T</kbd>. Topics run down the right; click one and they answer from
          what is actually true right now — what they are doing, the building they stand at, prices, the levy, offices, the
          war if there is one. <b>Highlighted words</b> in their answers are links: follow one to pursue it, and new topics
          join your list for good.</p>
          <p><b>Latest rumors</b> repeats what they picked up from neighbours and travelling traders — a town with no trade
          partners hears little. You can also type a question in your own words. The bar at the top shows how this town
          regards you.</p>`,
          see: ['people/both-ways', 'people/relations'],
        },
        {
          id: 'people/both-ways',
          title: 'Questions, stories, tales and leads',
          terms: ['their story', 'trade a tale', 'satchel', 'who told you', 'lead', 'journal'],
          body: `<p>Townsfolk don't only answer. One may bring something up — news, a grievance, or a <b>question for
          you</b>. What you say of yourself (where you're from, what you do, why you came) is remembered, called back on
          later visits, and gets round the town within a day. How you say it matters: a blunt answer suits the bold and
          grates on the gentle.</p>
          <ul>
          <li><b>Their story</b> — a chapter of their life each day, built from who they really are; the later chapters
          are kept for friends.</li>
          <li><b>Trade a tale…</b> — your satchel of stories (★ means first-hand). Tell the kind of tale their mood wants and
          a good telling earns one back. One a day.</li>
          <li><b>Who told you that?</b> — after a rumor, they name their source and how far they trust it, and a <b>lead</b>
          goes into your Journal. Find that person — often in another town — and <b>Ask about…</b> appears. Follow the trail
          far enough and you may reach someone who saw it happen.</li>
          <li><b>Where do you stand?</b> — some will tell you their view on the levy or the keeper. Agree, disagree or stay
          out of it; honest disagreement is never held against you, and a good argument can shift their politics.</li>
          </ul>
          <p>The <b>Journal…</b> topic keeps your leads, your tales, and what you have told people.</p>`,
        },
        {
          id: 'people/relations',
          title: 'Friendship, gifts and grudges',
          terms: ['friend', 'confidant', 'nemesis', 'gift', 'favor', 'apologize', 'regard'],
          body: `<p>Each townsperson keeps their own account of you, from <b>stranger</b> through <b>familiar</b>,
          <b>friend</b> and <b>confidant</b>, down through <b>cold</b> and <b>hostile</b> to <b>nemesis</b>. Time together,
          conversation (one a day counts), trade and help raise it; harm lowers it, and old feelings fade slowly.</p>
          <ul>
          <li><b>Give a gift…</b> — one a day; taste matters, so think about their trade and their dreams.</li>
          <li><b>Favours</b> — a friend may ask for something; deliver it for their gratitude.</li>
          <li><b>Apologize</b> — own what you did. Some will hear it; the proud will not, and may ask for deeds.</li>
          </ul>
          <p>A nemesis is dangerous: they may pay a bounty hunter to come after you. Friends greet you, tell you more, and
          can be convinced to move into your city.</p>`,
          see: ['town/hunters', 'groups/residents'],
        },
        {
          id: 'people/merchants',
          title: 'Buying from a merchant',
          terms: ['merchant', 'stall', 'counter', 'queue', 'shop'],
          body: `<p>Town merchants sell from stalls. Walk up and buy in person: the merchant serves one customer at a time
          (others queue), sets your goods aside while you decide, and hands them over. The same goods are on the bazaar
          terminal at that town. Money paid to a merchant is theirs — it enters the town's own economy.</p>
          <p>Someone who fears you may refuse to serve you.</p>`,
          see: ['money/bazaar'],
        },
        {
          id: 'people/events',
          title: 'Living events',
          terms: ['festival', 'feast', 'rite', 'ceremony', 'wedding', 'funeral', 'news'],
          body: `<p>Towns stage their own events: feasts, festivals, rites, weddings, funerals, elections, trials, war councils
          and send-offs. People arrive over a few minutes, gather at the venue, and the stage dresses itself — altars,
          tables, fire pits, lanterns. Walk in and watch; nobody minds.</p>
          <p>A toast tells you when one starts nearby, and live events show on the local map and the Atlas — click one to set
          a waypoint.</p>`,
          see: ['town/festivals', 'maps/local'],
        },
      ],
    },
    {
      id: 'town',
      title: 'Town life',
      blurb: 'The Town window, service posts, the contract board, bounties, fireworks, camps and festivals.',
      pages: [
        {
          id: 'town/window',
          title: 'The Town window',
          terms: ['town window', 'town life'],
          body: `<p><b>Town</b> on the menu bar opens one window with a tab for each part of town life: <b>Races</b>,
          <b>Service posts</b>, <b>Fireworks &amp; camp</b>, <b>Bounties</b>, <b>Contracts</b>, <b>Festivals</b>,
          <b>Wars</b> and <b>Weather</b>. Chips on the screen keep you posted while you are on duty, wanted, racing or at a
          festival.</p>`,
        },
        {
          id: 'town/posts',
          title: 'Service posts',
          terms: ['service post', 'duty', 'wage', 'customer', 'job'],
          body: `<p>Towns have posts a player can hold beside the townsfolk who staff them. Take one in the <b>Service
          posts</b> tab at its venue (route posts walk the plaza).</p>
          <ul>
          <li>You are paid for time actually on duty at the venue: 1 ⓒ a minute, up to two paid hours a day.</li>
          <li>Townsfolk come to you for the service: serve them within a few metres for a fee, their gratitude, and the town's
          score for that service.</li>
          <li>Leave a post unworked for three days and it lapses.</li>
          </ul>`,
        },
        {
          id: 'town/contracts',
          title: 'The contract board',
          terms: ['courier', 'escort', 'relief', 'parcel', 'collateral', 'contract board'],
          body: `<p>Every settlement has a contract board by its market. Players and towns post work there:</p>
          <ul>
          <li><b>Courier</b> — carry a sealed parcel to another town's board. You put up collateral; deliver and you get the
          reward and your collateral back. The recipient collects there.</li>
          <li><b>Escort</b> — stay close to a player, or to a town's trade caravan, until it arrives.</li>
          <li><b>Relief</b> — a hungry town asks for food; fill it from your stacks and you are paid by the unit.</li>
          </ul>
          <p>Posting costs a small fee, held in escrow with the reward. The <b>Contracts</b> tab lists yours.</p>`,
        },
        {
          id: 'town/bounties',
          title: 'Bounties',
          terms: ['bounty', 'wanted', 'terminal', 'take', 'mark', 'hunter'],
          body: `<p>Towns and cities have a bounty terminal by the guard post.</p>
          <ul>
          <li><b>Post</b> a bounty on a player: the reward plus a fee is held in escrow. Posting again on the same player tops
          it up. Cancel while no one has taken it to get the reward back.</li>
          <li><b>Take</b> a bounty: up to three hunters per bounty. Taking it means you and your mark may fight each other
          anywhere a contest is allowed — both ways. Down your mark and you are paid the whole reward.</li>
          <li>A hunter gets a rough trace of the mark's position every 45 seconds. A take that goes cold lapses.</li>
          <li><b>Hire a hunter</b> — pay the guild to send one of its own, stronger for more money.</li>
          </ul>
          <p>When someone hunts you, a <b>WANTED</b> chip shows.</p>`,
          see: ['town/hunters'],
        },
        {
          id: 'town/hunters',
          title: 'When a hunter comes for you',
          terms: ['stalker', 'followed', 'ambush', 'bounty hunter'],
          body: `<p>A townsperson who hates you, or a player who paid the guild, can send a <b>bounty hunter</b>. Hunters
          stalk: you may hear footsteps behind you, a radio crackle, see a figure that is gone when you turn. When the HUD
          says <i>possibly followed</i>, someone is.</p>
          <p>A hunter strikes when you are vulnerable — wounded, already fighting, or unarmed — and winds up like any blow. Turn
          and close on a stalking hunter and it runs for its ship. Fight back and it fights. Going indoors, to your own world,
          or into space puts the hunt on hold.</p>`,
        },
        {
          id: 'town/fireworks',
          title: 'Fireworks',
          terms: ['fireworks', 'light show', 'rocket'],
          body: `<p>Launch a rocket (5 ⓒ) or a whole show (60 ⓒ) from the <b>Fireworks &amp; camp</b> tab — everyone nearby
          sees and hears it. Cities and capitals put on their own night shows, festivals end with one, and the race finale
          lights the start line.</p>`,
        },
        {
          id: 'town/camps',
          title: 'Camps and fire circles',
          terms: ['camp', 'fire', 'campfire', 'warm', 'sit by the fire'],
          body: `<p>Taverns keep a fire circle with log seats; townsfolk gather at it in the evening. You can also <b>pitch a
          camp</b> of your own in the wilds (one at a time, for half an hour).</p>
          <p>{{key:interact}} at a fire walks you to a log and sits you down. Sitting by a fire lifts your social and rest needs
          and warms off storm exposure.</p>`,
        },
        {
          id: 'town/festivals',
          title: 'Seasonal festivals',
          terms: ['festival', 'keepsake', 'contest', 'melt feast', 'lantern night', 'harvest home', 'longlight'],
          body: `<p>The world's seasons each last six hours. In the second hour of each, every city and town holds its
          festival — the <b>Melt Feast</b>, the <b>Longlight Fair</b>, <b>Harvest Home</b> and <b>Lantern Night</b>.</p>
          <ul>
          <li>Spend five minutes at a festival and you take home its <b>keepsake</b> (a lift to fun and social).</li>
          <li>Each has a <b>contest</b> — the best produce, the longest fish, the most grain given, the finest rockets —
          paying 100, 60 and 30 ⓒ to the top three when at least two enter.</li>
          </ul>
          <p>The <b>Festivals</b> tab says what is on, where, and when the next one starts.</p>`,
        },
      ],
    },
    {
      id: 'money',
      title: 'Money',
      blurb: 'Credits, markets and the bazaar, and the bank.',
      pages: [
        {
          id: 'money/credits',
          title: 'Credits',
          terms: ['credits', 'ⓒ', 'wallet', 'ledger', 'earn'],
          body: `<p>Everything runs on one currency, the <b>credit</b> (ⓒ), shown at the top of the screen. Every movement
          of money goes through the server's double-entry ledger — it cannot be duplicated or lost in transit.</p>
          <p>You earn credits from bounties and contracts, duty boards in space, hauling and supply runs, service posts, the
          bazaar, exploration, festivals, racing and selling to towns; you spend them on ships, buildings, furnish packs,
          hosting, repairs, schematics and goods.</p>`,
        },
        {
          id: 'money/bazaar',
          title: 'The bazaar',
          terms: ['bazaar', 'market', 'listing', 'sell', 'buy', 'tax'],
          body: `<p>Every settlement's market has a <b>bazaar terminal</b> (walk up and press <kbd>B</kbd>). Players list
          resources, components, items and packed deeds there; a listed item leaves your inventory into escrow, and you are
          paid 95 % when it sells (5 % is tax). Prices are fixed — no auctions, no gambling.</p>
          <p>The terminal also carries the town merchants' stalls; buying from them pays the merchant directly. In space,
          each station runs its own exchange (see <a data-page="industry/exchange">Regional exchange</a>).</p>`,
        },
        {
          id: 'money/bank',
          title: 'The bank',
          terms: ['bank', 'account', 'deposit', 'withdraw', 'transfer', 'savings', 'term deposit', 'reserve'],
          body: `<p><b>Bank</b> on the menu bar opens your account at the <b>Commons Reserve</b>. Cash in and out happens at a
          branch — the bank terminal in any town or city ({{key:interact}} at it). Online, from anywhere: move money between
          accounts, send to another player, and lock savings into <b>term deposits</b>.</p>
          <p>Your balance earns interest paid out of what the bank earns on its loans — savings grow when borrowing is
          busy. Player cities can open banks of their own; deposits there are insured up to 25,000 ⓒ each should the city
          bank fail.</p>`,
          see: ['money/loans'],
        },
        {
          id: 'money/loans',
          title: 'Loans, credit and the safe box',
          terms: ['loan', 'credit', 'collateral', 'autopay', 'default', 'safe deposit box', 'auction'],
          body: `<ul>
          <li><b>Loans</b> — the bank quotes a rate from your history and what you pledge. The rate you accept is the rate you
          get. Repay in instalments; autopay takes each one from your account when due.</li>
          <li><b>Collateral</b> — a pledged item goes into the bank's keeping and comes home when the loan is repaid.</li>
          <li><b>Default</b> — miss payments and the bank sets your account against the debt, keeps the pledge, writes off
          the rest and refuses you credit for two weeks; your trustworthiness falls.</li>
          <li><b>The safe deposit box</b> — rent a box at a branch for things you do not want to carry.</li>
          <li><b>The auction block</b> — pledges from defaults are sold at a branch, the price stepping down over the days.</li>
          </ul>`,
        },
      ],
    },
    {
      id: 'groups',
      title: 'Groups, guilds and cities',
      blurb: 'Parties and friends, trading, guilds and their land, player cities and civilisations, ground wars.',
      pages: [
        {
          id: 'groups/social',
          title: 'Party, friends and safety',
          terms: ['party', 'friends', 'block', 'mute', 'report', 'social', 'g', 'messages'],
          body: `<p><kbd>G</kbd> (<b>Social</b>) is your party, friends, messages and safety in one window.</p>
          <ul>
          <li><b>Party</b> — <code>/invite name</code>; party chat with <code>/p</code>; pings for your party to see.</li>
          <li><b>Friends</b> — <code>/friend name</code>; see when they are on and where.</li>
          <li><b>Messages</b> — <code>/w name text</code> whispers anyone; conversations are private and kept for a
          week.</li>
          <li><b>Safety</b> — <b>block</b> stops someone whispering, waving, inviting, trading or challenging you (both
          ways); <b>mute</b> hides their chat for you alone; <b>report</b> flags them to the operators.</li>
          </ul>`,
        },
        {
          id: 'groups/trade',
          title: 'Trading face to face',
          terms: ['trade', 'swap', 'exchange items'],
          body: `<p><code>/trade name</code> opens a trade window with a player near you. Each side offers items, stacks and
          credits; both must confirm the same version of the offer, and then everything changes hands at once — or nothing
          does. What you offer is reserved while the trade is open: you cannot equip, sell, store or use it until the trade
          ends.</p>`,
        },
        {
          id: 'groups/guilds',
          title: 'Guilds',
          terms: ['guild', 'guild hall', 'charter', 'v', 'treasury', 'join'],
          body: `<p><kbd>V</kbd> (<b>Guilds</b>) opens your guild — tabs for the guild, its court, its property, members and
          the directory of every guild.</p>
          <p>To found one, place a <b>guild hall</b> deed where its first claim of land is free; the guild is chartered with
          you as its master. Others join at the hall's door (open door, or by application). Members <b>donate</b> to a
          treasury the court spends, and the guild pays a weekly upkeep from it.</p>`,
          see: ['groups/court', 'groups/land'],
        },
        {
          id: 'groups/court',
          title: 'The guild court',
          terms: ['guild master', 'council', 'election', 'motion', 'vote', 'recall'],
          body: `<p>A guild is run by its <b>master</b> and a <b>council</b> (two to seven seats, by size). Both are elected for a
          week at a time: the master by the most votes, the council by approval (vote for as many as there are seats).
          Incumbents stand again automatically.</p>
          <p>The court lays <b>motions</b> — spend from the treasury, expand the land, open or close the door, expel a member,
          recall the master — and a motion passes the moment most of the court votes yes.</p>`,
        },
        {
          id: 'groups/land',
          title: 'Guild land and property',
          terms: ['claim', 'territory', 'guild land', 'tier'],
          body: `<p>A guild claims a disc of land around its hall, 80 m across at first and up to 110 m as the guild grows (at
          5, 10 and 20 members, paid from the treasury). Only members may build inside it. Court members can raise
          <b>guild property</b> there from their own deeds, kept up by the treasury.</p>
          <p>If upkeep goes unpaid the claim shrinks a step. A guild whose last member leaves dissolves. Guild land shows on the
          Atlas's ⚜ layer.</p>
          <p>Townsfolk have trade guilds too: halls outside their towns where the members of a craft meet, elect their own
          court and look after each other.</p>`,
        },
        {
          id: 'groups/cities',
          title: 'Player cities',
          terms: ['city', 'city hall', 'mayor', 'rank', 'found a city', 'city console'],
          body: `<p>Place a <b>City Hall</b> deed and you have the start of a city (not too close to another). It becomes a real
          city when it has <b>five residents</b> — players who declare a home there, or townsfolk you convince to move in.</p>
          <p>Cities grow in <b>rank</b> with residents (5, 10, 15, 30, 40), and each rank widens the border (150 m up to
          450 m) and unlocks more civic buildings. <b>City</b> on the menu bar opens the console: overview, residents,
          buildings, taxes, defence and the city's civilisation, shown by what your role allows.</p>`,
          see: ['groups/residents', 'groups/civic'],
        },
        {
          id: 'groups/residents',
          title: 'Residents and the mayor',
          terms: ['resident', 'declare residence', 'mayor', 'invite versona', 'move in'],
          body: `<p>A player becomes a resident by declaring a house inside the border as home. Residents elect the <b>mayor</b>;
          the mayor appoints militia and sets taxes.</p>
          <p>Townsfolk move in only when <b>convinced</b>: a resident who is their friend, with a free bed in their house,
          invites them. They then live, work and trade in your city like any town. The mayor (or whoever invited them) can ask
          them to leave, and they go home.</p>
          <p>You can leave a city from its console; a mayor must hand over first.</p>`,
        },
        {
          id: 'groups/civic',
          title: 'Taxes, civic buildings and defence',
          terms: ['tax', 'civic', 'turret', 'militia', 'specialisation'],
          body: `<ul>
          <li><b>Taxes</b> — a property tax in credits fills the city treasury; a market tax feeds the townsfolk's
          economy.</li>
          <li><b>Civic buildings and decor</b> — placed by the mayor from their deeds, kept by the treasury, unlocked by
          rank.</li>
          <li><b>Defence</b> — walls and bunkers are solid; turrets in good repair fire on creatures and on townsfolk who
          attack, never on players.</li>
          <li><b>Specialisation</b> — the mayor gives the city a character.</li>
          </ul>`,
        },
        {
          id: 'groups/civs',
          title: 'Allegiance and civilisations',
          terms: ['alignment', 'civilisation', 'tithe', 'player civilisation'],
          body: `<p>A city can align with the Aurelians, the Boreans, or stand alone. Five cities that agree can found a
          <b>civilisation</b> of their own: one mayor proposes it, the others join, and at five members it forms. A
          civilisation can take a small tithe of its cities' tax income, and its militia count as militia in every member
          city. It stays out of the Aurelian–Borean war.</p>`,
        },
        {
          id: 'groups/wars',
          title: 'Ground wars between groups',
          terms: ['war', 'declare war', 'wardec', 'banner', 'surrender', 'peace'],
          body: `<p>A guild, a city, a civilisation or a group can declare war on another of its kind. The leader declares, paying a
          fee and staking credits; the other side answers. After a half-hour warm-up the war runs for three days.</p>
          <ul>
          <li>Members of the two sides may fight each other anywhere in the Commons outside sanctuaries.</li>
          <li>Downing an enemy scores a point. Holding your banner uncontested beside the enemy's seat — their City Hall or guild
          hall — for a minute scores five.</li>
          <li>Peace (both leaders), surrender or the clock ends it; the higher score takes the stakes.</li>
          </ul>
          <p>The Town window's <b>Wars</b> tab follows the score. Enemy fighters near you are marked.</p>`,
        },
      ],
    },
    {
      id: 'politics',
      title: 'Politics and war',
      blurb: 'The two civilisations, the Assembly, wars between towns, and your reputation.',
      pages: [
        {
          id: 'politics/civs',
          title: 'Towns, flags and conquest',
          terms: ['flag', 'conquest', 'siege', 'allegiance'],
          body: `<p>Every town belongs to one of the two peoples. Towns of the two sides quarrel over trade and old grudges;
          when talking fails, a town's council votes for war. A war is decided by battles, and a town that loses its gate
          twice changes flag. The capitals never fall.</p>`,
        },
        {
          id: 'politics/battles',
          title: 'Battles',
          terms: ['battle', 'outpost', 'militia', 'wounded', 'air war'],
          body: `<p>Each side musters its militia at an <b>outpost</b> on its side of the field and marches to meet the other.
          A battle lasts between a quarter and half an hour: lines advance, take cover, suppress, rush and fall back; the
          wounded walk back to their outpost's medics; relief columns march or fly in from friendly towns. Fighters and
          cruisers from both towns fight overhead and strafe the lines.</p>
          <p>Battles show on the Atlas as red dots, pulsing while the fight is on — go and watch. You can carry supplies to a
          side (next page).</p>`,
        },
        {
          id: 'politics/supply',
          title: 'Supply runs',
          terms: ['supply run', 'crates', 'munitions', 'medicine', 'rations'],
          body: `<p>During a battle each side's home town posts supply runs on its contract board. Take one there while you own a
          hovercraft, ride the crates to your side's outpost and unload within 35 m with {{key:interact}}. Munitions steady the
          line, medicine staffs the aid station, rations feed it, parts raise a turret — and you are paid by the distance.</p>`,
        },
        {
          id: 'politics/assembly',
          title: 'The Assembly',
          terms: ['assembly', 'federal', 'speaker', 'ministers', 'bill', 'levy', 'tribunal'],
          body: `<p><b>Assembly</b> on the menu bar opens the federal docket of your people: the Speaker, the ministers, the bill
          before the floor, public works and the Tribunal. The Assembly can set a levy on its towns, customs, relief for a
          starving town, public works, a drill or festival day, and war or peace by vote. The <b>Tribunal</b> hears grievances
          between towns.</p>
          <p>If you live in a town, your view counts in its vote.</p>`,
        },
        {
          id: 'politics/reputation',
          title: 'Reputation and trust',
          terms: ['reputation', 'trust', 'vouch', 'review', 'traitor', 'notoriety'],
          body: `<p>Four scores the server keeps about you: <b>trustworthiness</b>, <b>creator rating</b>, <b>social
          capital</b> and <b>notoriety</b>. Nobody can set them directly.</p>
          <ul>
          <li><b>Vouch</b> for someone by staking your own influence; you get it back if you withdraw — but if they betray a
          collaborator, everyone who vouched for them loses their stake.</li>
          <li><b>Reviews</b> — one per person per player, cautions included; negative reviews are never hidden.</li>
          <li>A proven betrayal costs heavily and leaves a thirty-day <b>Traitor</b> tag.</li>
          </ul>
          <p>Your Passport (<kbd>P</kbd>) shows your scores.</p>`,
        },
        {
          id: 'politics/pulse',
          title: 'Pulse, Compass and Orbits',
          terms: ['pulse', 'needs', 'compass', 'want', 'fear', 'wave', 'orbit'],
          body: `<p>Your <b>Pulse</b> is four needs — social, fun, creativity and rest — that drift down slowly and rise at
          places that meet them. A low need never punishes you; it only suggests where to go.</p>
          <p>The <b>Compass</b> (<kbd>C</kbd>) shows a Want and a Fear above you, if you choose — a way into a conversation with
          a stranger. Two people with compatible wants who wave at each other within ten metres both feel it. <b>Orbits</b>
          (<kbd>O</kbd>) are the people you have been spending time with; once you have actually done something together, an
          orbit travels with you between worlds.</p>`,
        },
      ],
    },
    {
      id: 'talk',
      title: 'Chat and voice',
      blurb: 'Typing, translation, voice nearby, and voice rooms.',
      pages: [
        {
          id: 'talk/chat',
          title: 'Chat',
          terms: ['chat', 'enter', 'whisper', 'commands', 'slash'],
          body: `<p><kbd>Enter</kbd> opens chat. What you type is heard by the people near you. Slash commands:</p>
          <table>
          <tr><td><code>/w name text</code></td><td>Whisper anyone, anywhere</td></tr>
          <tr><td><code>/p text</code> · <code>/invite name</code> · <code>/leave</code></td><td>Party chat and party</td></tr>
          <tr><td><code>/friend</code> · <code>/block</code> · <code>/mute</code> · <code>/report</code></td><td>Friends and safety</td></tr>
          <tr><td><code>/trade name</code></td><td>Open a trade</td></tr>
          <tr><td><code>/duel name</code> · <code>/arena 1-4</code> · <code>/enroll</code> · <code>/pvp</code></td><td>Contests</td></tr>
          <tr><td><code>/family</code></td><td>A townsperson's kin</td></tr>
          <tr><td><code>/translate on|off|language</code></td><td>Live translation</td></tr>
          <tr><td><code>/help</code></td><td>The full list</td></tr>
          </table>`,
        },
        {
          id: 'talk/translate',
          title: 'Translation',
          terms: ['translate', 'language', 'translation'],
          body: `<p>Turn on translation in Options → General → <b>Social</b> (or <code>/translate on</code>) and pick your
          language. Lines others write in another language appear with a translation beneath. Your browser translates on your
          device when it can; otherwise the server does.</p>`,
        },
        {
          id: 'talk/voice',
          title: 'Voice nearby',
          terms: ['voice chat', 'proximity voice', 'microphone'],
          body: `<p>With voice on, players near you can hear you, and you them — louder when close, from the direction they
          stand. Blocked players never hear you, nor you them.</p>`,
        },
        {
          id: 'talk/rooms',
          title: 'Voice rooms',
          terms: ['voice room', 'push to talk', 'stage', 'listen only', 'deafen'],
          body: `<p><b>Voice</b> on the menu bar opens voice rooms — conversations that do not depend on where you stand: your
          party's, guild's and city's rooms, open rooms anyone can join, and <b>stages</b> where a few speak and the rest
          listen.</p>
          <ul>
          <li><b>Push to talk</b> — {{key:voicePtt}} by default; or an open mic. Set it in the window's Settings.</li>
          <li><b>Listen only</b> — join without a microphone; turn it on later.</li>
          <li>Set each voice's volume, mute yourself, or deafen to hear nobody.</li>
          <li>Room moderators can mute or remove people; a removed person cannot return for a while.</li>
          </ul>`,
        },
      ],
    },
  ],
};
